/* eslint-disable import/no-cycle */
import { custInfoHasError, setCustomerInfo } from 'actions/customerInfoActions';
import { policyHasError, setPolicyInfo, setMotPlus } from 'actions/policyInfoActions';
import { setJwtToken, setPayfailAlertData, setShowPayfailAlert, setShowPayfailStepTwoAlert, setPayfailPopupShown } from 'actions/sessionActions';
import env from '@beam-australia/react-env';
import GigyaClient from 'auth/GigyaClient';
import * as APIConstants from 'constants/apiConstants';
import { GetVehiclesData } from 'services/VehicleService';
import { dispatch, getState } from 'store';
import * as commons from 'utils/commons';
import {
  anonymousPut, get, getEndpoint, patch, post, put, anonymousPost,
} from 'utils/httpHelpers';
import { loggerFunc } from 'utils/logger';
import { setCarInsurancePolicies, setCarInsurancePoliciesError } from 'actions/productsActions';
import { setDocumentsError, setDocuments } from '../actions/documentActions';
import { getMessage } from './MessageService';
import TileSets from '../utils/tileSets.enum';

const logger = loggerFunc('MyRACService');

const gigyaClient = new GigyaClient();

const getStateSession = () => {
  const state = getState();
  if (state && state.session) {
    return state.session;
  }
  return undefined;
};

const getCanonicalPayload = (method, correlationId, sessionId, payload) => (['GET', 'DELETE'].includes(method) ? null : JSON.stringify({
  correlationId,
  sessionId,
  payload,
}));

const anonymousSyncPost = (uri, queryParams, pathParams, payload) => {
  const session = getStateSession();
  const correlationId = commons.generateUID(8);
  const body = getCanonicalPayload('POST', correlationId, session ? session.sessionId : '', payload);
  const xhr = new XMLHttpRequest();
  xhr.open('POST', getEndpoint(`${env('WEB_EXP_HOST')}${uri}`, queryParams, pathParams), false);
  xhr.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');
  try {
    xhr.send(body);
    if (xhr.status !== 200) {
      throw new Error(xhr);
    } else {
      return xhr.response;
    }
  } catch (err) { // instead of onerror
    logger.error('anonymousSyncPost', err);
    throw err;
  }
};

const anonymousSyncPatch = (uri, queryParams, pathParams, payload) => {
  const session = getStateSession();
  const correlationId = commons.generateUID(8);
  const body = getCanonicalPayload('PATCH', correlationId, session ? session.sessionId : '', payload);
  const xhr = new XMLHttpRequest();
  xhr.open('PATCH', getEndpoint(`${env('WEB_EXP_HOST')}${uri}`, queryParams, pathParams), false);
  xhr.setRequestHeader('ApiKey', env('API_KEY'));
  // Basic authentication opted for as JWT tokens can't be validated at this point
  xhr.setRequestHeader('Authorization', `Basic ${env('USER_BASIC_AUTH')}`);
  xhr.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');
  try {
    xhr.send(body);
    if (xhr.status !== 204) {
      throw new Error(xhr);
    } else {
      return xhr.response;
    }
  } catch (err) { // instead of onerror
    logger.error('anonymousSyncPatch', err);
    throw err;
  }
};

// Default customer login method
const getCustomerInfo = async (customerId) => {
  const pathParams = {
    customerId,
  };
  return get(APIConstants.API_CUSTOMER, null, pathParams, false);
};

// Fallback customer login method when 'clientId' from gigya is returned as null (we store this value in our state as 'customerId')
const getCustomerInfoByMemberNo = async (memberNo, uid) => {
  const xhr = new XMLHttpRequest();
  xhr.open('GET', getEndpoint(`${env('WEB_EXP_HOST')}${APIConstants.API_CUSTOMER_FINDBYMEMBERSHIPID(memberNo, uid)}`), false);
  xhr.setRequestHeader('ApiKey', env('API_KEY'));
  // Basic authentication opted for as JWT tokens can't be validated at this point
  xhr.setRequestHeader('Authorization', `Basic ${env('USER_BASIC_AUTH')}`);
  xhr.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');
  try {
    xhr.send();
    if (xhr.status !== 200) {
      throw new Error(xhr);
    } else {
      return JSON.parse(xhr.response);
    }
  } catch (err) {
    logger.error('getCustomerInfoByMemberNo', err);
    throw err;
  }
};

// Get and set a new JWT token
export const GetJWT = async (expirationTime) => new Promise((resolve, reject) => {
  gigyaClient.getJWT((jwtResponse) => {
    if (jwtResponse.status === 'OK') {
      if (jwtResponse.id_token !== null) {
        dispatch(setJwtToken(jwtResponse.id_token));
        resolve(jwtResponse.id_token);
      } else {
        this.handleError(new Error('id_token not found'), getMessage());
        reject(new Error('id_token not found'), getMessage());
      }
    } else {
      this.handleError(new Error(jwtResponse.status), getMessage());
      reject(new Error(jwtResponse.status), getMessage());
    }
  }, expirationTime);
});

const getCustomer = async (customerId) => new Promise((resolve, reject) => {
  logger.debug('retrieving customer data');
  getCustomerInfo(customerId)
    .then((customer) => {
      logger.debug('storing customer data response', customer);
      dispatch(setCustomerInfo(customer.payload));
      resolve({ customerId: customer.payload.Id, membershipNumber: customer.payload.membershipNumber });
    })
    .catch((error) => {
      logger.error('retrieving customer data failed due to', error);
      dispatch(custInfoHasError(true));
      reject(error);
    });
});

export const GetPolicyInfo = async () => {
  const hackRACLiteCoverType = (cover = '') => (
    cover.includes('RAC Lite') ? 'RAC Pay As You Go' : cover
  );

  try {
    logger.debug('retrieving policy data');

    const policies = (await get(APIConstants.API_POLICY)).payload;
    // We currently don't support multi policy so we just take the first policy in the list (there will only be a max of one for the forseeable)
    const policy = policies[0]

    if (!policy) {
      dispatch(setPolicyInfo({}));
      return {};
    }

    const reduxPolicy = {
      ...policy,
      CoverType: {
        ...(policy?.CoverType || {}),
        Title: hackRACLiteCoverType(policy.CoverType?.Title),
        FullDescription: hackRACLiteCoverType(policy.CoverType?.FullDescription),
      },
    }

    logger.debug('storing policy');
    dispatch(setPolicyInfo(reduxPolicy));
    return policy;
  } catch (error) {
    logger.error('retrieving policy data failed due to', error);
    dispatch(policyHasError(true));
    throw error;
  }
};

/*
  Policy info needs to be refreshed else otherwise the customer can not update additional members
  in the same session because the Drive sequence id will be wrong. This is a convenience function for
  usage just in this service.
 */
export const GetLatestPolicyInfo = async () => {
  await GetPolicyInfo();
};

export const GetMotPlus = async (customerId, userId) => {
  try {
    const pathParams = {
      customerId,
      userId,
    };
    logger.debug('retrieving mot plus status');
    const response = await get(APIConstants.API_MOT_PLUS, null, pathParams);
    logger.debug('storing mot plus status');
    dispatch(setMotPlus(response?.payload));
  } catch (error) {
    logger.error('failed to retrieve mot plus data due to', error);
    dispatch(policyHasError(error));
  }
};

export const GetDocuments = async (customerId, policyId) => {
  try {
    const pathParams = {
      customerId,
      policyId,
    };
    logger.debug('retrieving customer documents');
    const documents = await get(APIConstants.API_DOCUMENTS_LIST, null, pathParams);
    logger.debug('storing customer documents');
    dispatch(setDocuments(documents));
  } catch (error) {
    // we just suppress the error here because we don't want to stop the site
    logger.error('failed to retrieve the documents data due to', error);
    dispatch(setDocumentsError(error));
  }
};

export const GetLatestPolicyDocuments = async () => {
  const {
    session: { customerId },
    policy: { policyInfo },
  } = getState();

  await GetDocuments(customerId, policyInfo.PolicyId);
};

export const GetCustomerData = async (customerId, memberNo, uid) => {
  // If 'customerId' is null at this point then the 'memberNo' and 'uid' will be used to action a separate API call in getCustomerInfoByMemberNo()
  const [customer, policy, carInsurancePolicies] = await Promise.all(
    [
      getCustomer(customerId, memberNo, uid),
      GetPolicyInfo(),
      GetInsurancePolicy(),
      GetVehiclesData(),
    ],
  );

  const { customerId: cid, membershipNumber } = customer

  if (policy.PolicyId && cid) {
    await Promise.all([
      GetDocuments(cid, policy.PolicyId),
      GetMotPlus(cid, uid),
    ])
  }

  return {
    policy,
    carInsurancePolicies,
    customerId: cid,
    membershipNumber,
  }
};

export const GetCustomerDataByMemberNo = async (memberNo, uid) => new Promise((resolve, reject) => {
  // where 'customerId' is null use alternative login method passing 'memberNo' and 'uid'
  logger.debug('retrieving customer data using memberNo');
  getCustomerInfoByMemberNo(memberNo, uid)
    .then((customer) => {
      logger.debug('storing customer data response', customer);
      dispatch(setCustomerInfo(customer.payload));
      resolve(customer.payload);
      return customer.payload;
    })
    .catch((error) => {
      logger.error('retrieving customer data failed due to', error);
      dispatch(custInfoHasError(true));
      reject(error);
      return error;
    });
});

export const UpdateEmailAddress = async (emailAddress, membershipNo) => {
  const pathParams = {
    membershipNo,
  };

  const payload = {
    Email: emailAddress,
  };
  // TODO: Return the promise and handle it that way instead of waiting for it here.
  // eslint-disable-next-line no-return-await
  return await put(APIConstants.API_USER_EMAIL, null, pathParams, payload);
};

export const UpdateRegistrationEmailAddress = (emailAddress, membershipNumber) => {
  const pathParams = {
    membershipNumber,
  };

  const payload = {
    Email: emailAddress,
  };
  return anonymousSyncPatch(APIConstants.API_CUSTOMER_CONTACT_EMAIL, null, pathParams, payload);
};

export const UpdateCustomerAddress = async (address, customerId) => {
  const pathParams = {
    customerId,
  };
  // TODO: Return the promise and handle it that way instead of waiting for it here.
  // eslint-disable-next-line no-return-await
  return await patch(APIConstants.API_CUSTOMER_CONTACT_ADDRESS, null, pathParams, address);
};

export const GetAddressById = async (addressId) => {
  const pathParams = {
    addressId,
  };
  // TODO: Return the promise and handle it that way instead of waiting for it here.
  // eslint-disable-next-line no-return-await
  return await get(APIConstants.API_CUSTOMER_ADDRESS_RETRIEVE, null, pathParams);
};

export const GetAddressesByPostCode = async (postCode) => {
  const pathParams = {
    postCode,
  };
  // TODO: Return the promise and handle it that way instead of waiting for it here.
  // eslint-disable-next-line no-return-await
  return await get(APIConstants.API_CUSTOMER_ADDRESS_SEARCH, null, pathParams);
};

export const UpdateCommunicationsPref = async (customerId, preferences) => {
  const pathParams = {
    customerId,
  };
  // eslint-disable-next-line no-return-await
  return await patch(APIConstants.API_CUSTOMER_COMMS, null, pathParams, preferences);
};

export const UpdateDocumentPrefs = async (customerId, payload) => {
  const pathParams = {
    customerId,
  };
  // TODO: Return the promise and handle it that way instead of waiting for it here.
  // eslint-disable-next-line no-return-await
  return await patch(APIConstants.API_CUSTOMER_CONTACT_DOCUMENT_PREFS, null, pathParams, payload);
};

export const UpdateDateOfBirth = async (customerId, payload) => {
  const pathParams = {
    customerId,
  };
  // TODO: Return the promise and handle it that way instead of waiting for it here.
  // eslint-disable-next-line no-return-await
  return await patch(APIConstants.API_CUSTOMER_DOB, null, pathParams, payload);
};

// eslint-disable-next-line no-return-await
export const UpdateAutorenewal = async (payload) => await patch(APIConstants.API_AUTORENEWAL_PREFS, null, null, payload);

/**
 * Deliberately a separate call from UpdateAutorenewal - the preference is saved
 * the moment the member confirms, so abandoning the survey must never lose the
 * opt-out. The member is resolved from the JWT, hence no customer or policy id.
 */
// eslint-disable-next-line no-return-await
export const SubmitAutoRenewFeedback = async (payload) => await post(APIConstants.API_AUTORENEWAL_FEEDBACK, null, null, payload);

export const GetDocument = async (customerId, docType, documentId) => {
  const pathParams = {
    customerId,
    docType,
    documentId,
  };
  // TODO: Return the promise and handle it that way instead of waiting for it here.
  // eslint-disable-next-line no-return-await
  return await get(APIConstants.API_DOCUMENT, null, pathParams);
};

export const UpdateCustomerPhoneNumber = async (customerId, payload) => {
  const pathParams = {
    customerId,
  };
  // TODO: Return the promise and handle it that way instead of waiting for it here.
  // eslint-disable-next-line no-return-await
  return await patch(APIConstants.API_CUSTOMER_CONTACT_PHONE, null, pathParams, payload);
};

// eslint-disable-next-line
export const UpdatePassword = async (payload) => {
  // TODO: Return the promise and handle it that way instead of waiting for it here.
  // eslint-disable-next-line no-return-await
  return await put(APIConstants.API_PASSWORD_UPDATE, null, null, payload);
};

// eslint-disable-next-line
export const ResetPassword = async (payload) => {
  // TODO: Return the promise and handle it that way instead of waiting for it here.
  // eslint-disable-next-line no-return-await
  return await anonymousPut(APIConstants.API_PASSWORD_RESET, null, null, payload);
};

export const ValidateMember = (memberNo, payload) => {
  const pathParams = {
    membershipNo: memberNo,
  };
  return anonymousSyncPost(APIConstants.API_USER_VALIDATE, null, pathParams, payload);
};

export const ValidateAndGetMagicLink = (payload) => anonymousPost(APIConstants.API_USER_MAGICLINK_CREATE, null, null, payload);
export const IsEmailAvailable = (payload) => anonymousSyncPost(APIConstants.API_EMAIL_AVAILABLE, null, null, payload);

export const UpdateAdditionalMember = async (customerId, policyId, payload) => {
  const pathParams = {
    policyId,
  };

  const queryParam = {
    customerId,
  };

  // TODO: Return the promise and handle it that way instead of waiting for it here.
  // eslint-disable-next-line no-return-await
  return await post(APIConstants.API_POLICY_UPDATEADDITIONALMEMBERS, queryParam, pathParams, payload);
};

export const GetTescoLoginJID = async (customerId, membershipNo) => {
  const pathParams = {
    customerId,
  };

  const queryParam = {
    membershipNo,
  };

  // TODO: Return the promise and handle it that way instead of waiting for it here.
  // eslint-disable-next-line no-return-await
  return await get(APIConstants.API_CUSTOMER_JID_TESCO_LOGIN, queryParam, pathParams);
};

export const GetLayoutGrid = async (customerId, membershipId, tileSet, mobile) => {
  const pathParams = {
    customerId,
    membershipId,
  };

  if (mobile) {
    switch (tileSet) {
      case TileSets.membershipOverview: return get(APIConstants.API_MEMBERSHIP_OVERVIEW_GRID_LAYOUT_MOBILE, null, pathParams, false);
      default: return get(APIConstants.API_MEMBERSHIP_OVERVIEW_GRID_LAYOUT_MOBILE, null, pathParams, false);
    }
  }

  switch (tileSet) {
    case TileSets.membershipOverview: return get(APIConstants.API_MEMBERSHIP_OVERVIEW_GRID_LAYOUT, null, pathParams, false);
    case TileSets.documentsPageAds: return get(APIConstants.API_DOCUMENT_GRID_LAYOUT, null, pathParams, false);
    default: return get(APIConstants.API_MEMBERSHIP_OVERVIEW_GRID_LAYOUT, null, pathParams, false);
  }
};

export const GetStaticContent = async (contentTypeKey) => {
  const pathParams = {
    contentTypeKey,
  };
  logger.debug('retrieving static content ');

  return get(APIConstants.API_STATIC_CONTENT, null, pathParams, false);
};

export const GetProductUpgradeData = async (customerId, productName) => {
  const pathParams = {
    customerId,
    productName,
  };
  logger.debug('retrieving product upgrade data for the product: ', productName);

  return get(APIConstants.API_TACTICAL_UPGRADE_DATA, null, pathParams, false);
};

export const GetInsurancePolicy = async () => {
  logger.debug('retrieving insurance data');

  try {
    const response = await get(APIConstants.API_CAR_INSURANCE, null, null, false);
    dispatch(setCarInsurancePolicies(response.payload))

    return response.payload
  } catch (e) {
    dispatch(setCarInsurancePoliciesError())

    return []
  }
};

export const GetPolicyStatus = async () => {
  logger.debug('retrieving policy status');

  return get(APIConstants.API_POLICY_STATUS, null, null, false);
};

export const GetPolicyDetails = async () => {
  logger.debug('retrieving policy details');

  return get(APIConstants.API_POLICY_DETAILS, null, null, false);
};

export const GetPolicyRenewalDetails = async () => {
  logger.debug('retrieving policy renewal details');

  return get(APIConstants.API_POLICY_RENEWAL_DETAILS, null, null, false);
};

export const GetProductUpgradeList = async () => {
  logger.debug('retrieving product upgrade list');

  return get(APIConstants.API_PRODUCT_UPGRADES, null, null, false);
};

export const GetProductsIncludedInPolicy = async () => {
  logger.debug('retrieving products included in policy');

  return get(APIConstants.API_PRODUCTS_INCLUDED, null, null, false);
};

export const SaveProductUpgradeProduct = async (upgradeProductPayload) => post(APIConstants.API_PRODUCT_UPGRADE_CONFIRMATION, null, null, upgradeProductPayload);

export const GetPaymentId = async (customerId, productName, membershipNumber, price, vehicleRegistration, extraPersonTitle, extraPersonFirstname, extraPersonSurname, extraPersonDateOfBirth) => {
  logger.debug('retrieving payment ID for Global Pay Iframe.');

  const payload = {
    customerId,
    productName,
    membershipNumber,
    price,
    vehicleRegistration,
    extraPersonTitle,
    extraPersonFirstname,
    extraPersonSurname,
    extraPersonDateOfBirth,
  };

  return post(APIConstants.API_PRODUCT_CREATE_PAYMENT_ID, null, null, payload);
};

export const UpdateProductUpgradePayment = async (paymentId, customerId, productName, membershipNumber, price) => {
  logger.debug('updating payment in payment database.');

  // The 'vehicleRegistration' value is optional and null by default unless required to be passed from ProductUpgradeJourney
  const payload = {
    customerId,
    productName,
    membershipNumber,
    price,
  };

  return put(APIConstants.API_PRODUCT_PAYMENT_STATUS(paymentId), null, null, payload);
};

export const GetRenewalData = async (oneMonthFreeSearchParam) => {
  logger.debug('retrieving renewal membership data');

  return get(APIConstants.API_RENEWAL_DATA, oneMonthFreeSearchParam ? { [APIConstants.URL_PARAMETERS.ONE_MONTH_FREE]: true } : null, null, false, APIConstants.version.V2);
};

export const GetRenewalPaymentIdAndLockToken = async () => {
  logger.debug('retrieving payment id for ssp renewal');

  const { uid, sessionId, isTescoLogin } = getState().session;
  const { usePreviousCardDetails } = getState().renewal.basket;

  const payload = {
    ...getState().renewal.paymentPayload,
    UsePreviousCardDetails: usePreviousCardDetails,
    isTescoLogin,
    LockInformation: {
      UserId: uid,
      SessionId: sessionId,
    },
  };

  return post(APIConstants.API_RENEWAL_CREATE_PAYMENT_ID, null, null, payload);
};

export const SetRenewalPaymentStatus = async (Price, PaymentId, LockToken) => {
  logger.debug('updating payment status in payment database');

  const payload = {
    Price,
    PaymentId,
    LockToken,
  };

  return put(APIConstants.API_RENEWAL_PAYMENT_STATUS, null, null, payload);
};

export const SetRenewalDirectDebitStatus = async (CustomerId, MembershipNumber, PolicyNumber, Price, RiskVersion, LockToken, AccountHolder, AccountNumber, Sortcode) => {
  logger.debug('updating direct debit status in database');

  const payload = {
    CustomerId,
    MembershipNumber,
    PolicyNumber,
    Price,
    RiskVersion,
    LockToken,
    AccountHolder,
    AccountNumber,
    Sortcode,
  };

  const { chosenMonthlyFlex } = getState().renewal.basket;

  // Monthly flex must use endpoint that will only validate direct debit data
  const url = chosenMonthlyFlex
    ? APIConstants.API_RENEWAL_DIRECT_VALIDATE_ONLY
    : APIConstants.API_RENEWAL_DIRECT_DEBIT_STATUS;

  return put(url, null, null, payload);
};

export const ReleaseLockToken = async (lockToken) => {
  logger.debug(`releasing lock token ${lockToken}`);
  const payload = lockToken;
  return post(APIConstants.API_RENEWAL_RELEASE_LOCK, null, null, payload);
};

export const GetRenewalPopup = async (customerId) => {
  const pathParams = {
    customerId,
  };
  logger.debug('retrieving renewal popup eligibility and data');

  return get(APIConstants.API_RENEWAL_POPUP, null, pathParams, false, APIConstants.version.V3);
};

export const GetRenewalEligibility = async (customerId) => {
  const pathParams = {
    customerId,
  };
  logger.debug('retrieving renewal eligibility information');

  return get(APIConstants.API_RENEWAL_ELIGIBILITY, null, pathParams, false, APIConstants.version.V3);
};

export const GetPaymentStatus = async (paymentId) => {
  logger.debug('retrieving current payment status: ', paymentId);

  return get(APIConstants.API_PRODUCT_PAYMENT_STATUS(paymentId), null, {}, false);
};

export const GetSMRContent = async () => {
  logger.debug('retrieving SMR content ');

  return get(APIConstants.API_SMR_CONTENT, null, {}, false);
};

export const GetLoyaltyRewards = async () => {
  logger.debug('retrieving RLR data');

  return get(APIConstants.API_RLR_OFFER, null, null, false, APIConstants.version.V1_NONCANONICAL);
};

export const ClaimLoyaltyReward = async (selectedReward, surname, memberNo, sourceOfClaim, nominatedVehicleForVSOE) => {
  logger.debug('claiming RLR');

  const payload = {
    rewardCodeClaimed: selectedReward,
    withRetainAttemptIfApplicable: true,
    membershipNumber: memberNo,
    surname,
    sourceOfClaim,
    nominatedVehicleForVSOE,
  };

  return put(APIConstants.API_RLR_OFFER_CLAIM, null, null, payload, false, APIConstants.version.V1_NONCANONICAL);
};

export const GetCarInsuranceAdvertHeroCard = async () => {
  try {
    const response = await get(APIConstants.API_HERO_CARD, null, null, false, APIConstants.version.V1_NONCANONICAL);
    return response.payload.carInsuranceWeb.carinsuranceAdvert[0];
  } catch (e) {
    logger.error('error retrieving hero card data')

    return undefined;
  }
};

export const GetVoucherDetails = async () => {
  try {
    const response = await get(APIConstants.API_VOUCHERS, null, null, false, APIConstants.version.V1_NONCANONICAL);
    return response.payload;
  } catch (e) {
    logger.error('error retrieving voucher details')

    return undefined;
  }
};

export const UserEventTracking = async (payload) => {
  logger.debug(`Cosmos event: ${payload.eventType} sent`);
  return post(APIConstants.API_MAU_EVENT_TRACKING, null, null, payload, false, APIConstants.version.V1_NONCANONICAL);
};

export const GetVulnerabilityData = async () => {
  try {
    const response = await get(APIConstants.API_VULNERABILITIES, null, null, false, APIConstants.version.V1_NONCANONICAL);
    return response.payload;
  } catch (e) {
    logger.error('error fetching vulnerabilities', e)

    return undefined;
  }
};

export const GetUserVulnerabilityData = async () => {
  try {
    const response = await get(APIConstants.API_USER_VULNERABILITIES, null, null, false, APIConstants.version.V1_NONCANONICAL);
    return response.payload;
  } catch (e) {
    logger.error('error fetching user-vulnerabilities', e)

    return undefined;
  }
};

export const PostUserVulnerabilityData = async (payload) => {
  try {
    const response = await post(APIConstants.API_USER_VULNERABILITIES, null, null, payload, false, APIConstants.version.V1_NONCANONICAL);

    return response;
  } catch (e) {
    logger.error('error posting user vulnerability data', e)

    return undefined;
  }
};

export const GetMarketingAllowedPreferences = async () => {
  try {
    const response = await get(APIConstants.API_ALLOWED_PREFERENCES, null, null, false, APIConstants.version.V1_NONCANONICAL);
    return response.payload;
  } catch (e) {
    logger.error('error fetching marketing allowed preferences', e)

    return undefined;
  }
};

export const GetUserMarketingPreferences = async () => {
  try {
    const response = await get(APIConstants.API_GET_USER_PREFERENCES, null, null, false, APIConstants.version.V1_NONCANONICAL);
    return response.payload;
  } catch (e) {
    logger.error('error fetching user marketing preferences', e)

    return undefined;
  }
};

export const UpdateUserMarketingPreferences = async () => {
  try {
    const response = await put(APIConstants.API_UPDATE_USER_PREFERENCES, null, null, null, false, APIConstants.version.V1_NONCANONICAL);

    return response;
  } catch (e) {
    logger.error('error updating user marketing preferences', e)

    return undefined;
  }
};

export const GetPayfailInformation = async () => {
  logger.debug('retrieve payfail information');
  const { payfailPopupShown } = getState().session;

  try {
    const response = await get(APIConstants.API_PAYFAIL_ELIGIBILITY, null, null, false, APIConstants.version.V1);
    if (response?.payload) {
      const data = response.payload;

      // Members who can't resolve online still get the alert and pop-up, with a "call us" message
      if (data.hasPayfailActivity) {
        dispatch(setShowPayfailAlert(true));
        dispatch(setPayfailAlertData(data));
      } else {
        dispatch(setShowPayfailAlert(false));
      }

      if (payfailPopupShown === undefined) {
        dispatch(setPayfailPopupShown(false));
      }

      if (data.canProceedDireclyToReactivation) {
        dispatch(setShowPayfailStepTwoAlert(true));
      }

      return response;
    }

    return undefined;
  } catch (e) {
    logger.error('error retrieving payfail information')
    return false;
  }
};
